import '../styles/teasers.scss';
import { drawCoverImage as sharedDrawCoverImage } from './modules/pixel-cover-draw.js';
import { initStandaloneTeaserPixelate } from './modules/standalone-teaser-pixelate.js';

document.addEventListener('DOMContentLoaded', () => {
    const containers = document.querySelectorAll('.f-teaser-container');
    const featuredTeasers = Array.from(document.querySelectorAll('.f-featured-teaser'));
    const overlayTeasers = Array.from(document.querySelectorAll('.f-overlay-teaser'));

    if (!containers.length && !featuredTeasers.length && !overlayTeasers.length) {
        return;
    }

    const updaters = [];

    // Smooth continuous scroll expansion for featured teaser cards on mobile (< 1025px)
    if (featuredTeasers.length > 0) {
        const updateFeaturedCards = () => {
            if (window.innerWidth >= 1025) {
                featuredTeasers.forEach((teaser) => {
                    const card = teaser.querySelector('.f-featured-teaser__card');
                    if (card) {
                        card.style.removeProperty('--expand-progress');
                    }
                });
                return;
            }

            const viewportHeight = window.innerHeight;
            const enterPoint = viewportHeight * 0.95;
            const fullPoint = viewportHeight * 0.45;

            featuredTeasers.forEach((teaser) => {
                const card = teaser.querySelector('.f-featured-teaser__card');
                if (!card) {
                    return;
                }

                const cardRect = card.getBoundingClientRect();

                let factor = 0;
                if (cardRect.top <= fullPoint) {
                    factor = 1;
                } else if (cardRect.top < enterPoint) {
                    factor = (enterPoint - cardRect.top) / (enterPoint - fullPoint);
                }

                card.style.setProperty('--expand-progress', Math.max(0, Math.min(1, factor)).toFixed(3));
            });
        };

        updaters.push(updateFeaturedCards);
        updateFeaturedCards();
    }

    containers.forEach((container) => {
        const teasers = Array.from(container.querySelectorAll('.f-featured-teaser, .f-overlay-teaser'));
        if (teasers.length <= 1) {
            return;
        }

        teasers[0].classList.add('is-first-teaser');
        teasers[teasers.length - 1].classList.add('is-last-teaser');

        // Tracks which teasers (by index) are overlay-teasers with a dark overlay.
        const overlayFlags = teasers.map(() => false);

        let sharedMedia = container.querySelector('.f-teaser-container__media');
        if (!sharedMedia) {
            sharedMedia = document.createElement('div');
            sharedMedia.className = 'f-teaser-container__media';
            container.insertBefore(sharedMedia, container.firstChild);

            teasers.forEach((teaser, index) => {
                const media = teaser.querySelector('.f-featured-teaser__media, .f-overlay-teaser__media');
                if (media) {
                    media.setAttribute('data-teaser-index', String(index));
                    sharedMedia.appendChild(media);
                }

                const overlay = teaser.querySelector('.f-overlay-teaser__overlay');
                if (overlay) {
                    overlayFlags[index] = true;
                    overlay.remove();
                }
            });

            if (overlayFlags.some(Boolean)) {
                const sharedOverlay = document.createElement('div');
                sharedOverlay.className = 'f-teaser-container__overlay';
                sharedMedia.appendChild(sharedOverlay);
            }
        }

        const sharedOverlay = sharedMedia.querySelector('.f-teaser-container__overlay');

        let canvas = sharedMedia.querySelector('.f-teaser-container__canvas');
        if (!canvas) {
            canvas = document.createElement('canvas');
            canvas.className = 'f-teaser-container__canvas';
            sharedMedia.appendChild(canvas);
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        const offscreenCanvas = document.createElement('canvas');
        const offscreenCtx = offscreenCanvas.getContext('2d');

        const getActiveMediaElement = (idx) => {
            const holder = sharedMedia.querySelector(`[data-teaser-index="${idx}"]`) || teasers[idx];
            if (!holder) {
                return null;
            }
            const isMobile = window.innerWidth < 768;
            if (isMobile) {
                const mobileEl = holder.querySelector('.f-overlay-teaser__video--mobile, .f-overlay-teaser__image--mobile');
                if (mobileEl) {
                    return mobileEl;
                }
            } else {
                const desktopEl = holder.querySelector('.f-overlay-teaser__video--desktop, .f-overlay-teaser__image--desktop');
                if (desktopEl) {
                    return desktopEl;
                }
            }
            return holder.querySelector('img, video');
        };

        const getActiveImages = () => teasers.map((_, idx) => getActiveMediaElement(idx));

        const allMedia = sharedMedia.querySelectorAll('img, video');
        allMedia.forEach((el) => {
            if (el.tagName === 'IMG' && !el.complete) {
                el.addEventListener('load', () => draw());
            } else if (el.tagName === 'VIDEO') {
                el.addEventListener('loadeddata', () => draw());
                el.addEventListener('play', () => checkVideoLoop());
            }
        });

        let canvasWidth = 0;
        let canvasHeight = 0;

        const resizeCanvas = () => {
            const rect = sharedMedia.getBoundingClientRect();
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const w = Math.round(rect.width * dpr);
            const h = Math.round(rect.height * dpr);

            if (canvasWidth !== w || canvasHeight !== h) {
                canvasWidth = w;
                canvasHeight = h;
                canvas.width = canvasWidth;
                canvas.height = canvasHeight;
            }
        };

        const drawCoverImage = (img, pixelSize = 1, opacity = 1) => {
            sharedDrawCoverImage({
                ctx,
                offscreenCtx,
                offscreenCanvas,
                img,
                canvasWidth,
                canvasHeight,
                pixelSize,
                opacity,
            });
        };

        let isIntersecting = false;
        let animationFrameId = null;
        let isContainerOffscreen = true;
        let segIndex = 0;
        let weightA = 1;
        let weightB = 0;
        let pixelSizeA = 1;
        let pixelSizeB = 1;

        const updateLayout = () => {
            resizeCanvas();
            if (!canvasWidth || !canvasHeight) {
                return;
            }

            const viewportHeight = window.innerHeight;
            const containerRect = container.getBoundingClientRect();

            const isEnded = containerRect.bottom <= viewportHeight;
            container.classList.toggle('is-ended', isEnded);

            if (containerRect.bottom <= 0 || containerRect.top >= viewportHeight) {
                isContainerOffscreen = true;
                return;
            }
            isContainerOffscreen = false;

            const viewportCenter = viewportHeight / 2;
            const centers = teasers.map((t) => {
                const r = t.getBoundingClientRect();
                return r.top + r.height / 2;
            });

            let progress = 0;

            if (viewportCenter <= centers[0]) {
                segIndex = 0;
                progress = 0;
            } else if (viewportCenter >= centers[centers.length - 1]) {
                segIndex = centers.length - 2;
                progress = 1;
            } else {
                for (let i = 0; i < centers.length - 1; i++) {
                    if (viewportCenter >= centers[i] && viewportCenter < centers[i + 1]) {
                        segIndex = i;
                        const span = centers[i + 1] - centers[i];
                        progress = span > 0 ? (viewportCenter - centers[i]) / span : 0;
                        break;
                    }
                }
            }

            const maxPixelSize = Math.max(50, Math.min(window.innerWidth / 12, 110));
            const transitionStart = 0.3;
            const transitionEnd = 0.7;

            if (progress <= transitionStart) {
                weightA = 1;
                weightB = 0;
                pixelSizeA = 1;
                pixelSizeB = 1;
            } else if (progress >= transitionEnd) {
                weightA = 0;
                weightB = 1;
                pixelSizeA = 1;
                pixelSizeB = 1;
            } else {
                const t = (progress - transitionStart) / (transitionEnd - transitionStart);
                const crossfadeStart = 0.4;
                const crossfadeEnd = 0.6;

                const pA = Math.min(1, t / 0.5);
                pixelSizeA = 1 + Math.pow(pA, 2) * maxPixelSize;

                const pB = Math.max(0, Math.min(1, (1 - t) / 0.5));
                pixelSizeB = 1 + Math.pow(pB, 2) * maxPixelSize;

                if (t < crossfadeStart) {
                    weightA = 1;
                    weightB = 0;
                } else if (t > crossfadeEnd) {
                    weightA = 0;
                    weightB = 1;
                } else {
                    const blend = (t - crossfadeStart) / (crossfadeEnd - crossfadeStart);
                    weightA = 1 - blend;
                    weightB = blend;
                }
            }

            if (sharedOverlay) {
                const overlayOpacity = (overlayFlags[segIndex] ? weightA : 0) + (overlayFlags[segIndex + 1] ? weightB : 0);
                sharedOverlay.style.opacity = String(Math.max(0, Math.min(1, overlayOpacity)));
            }
        };

        const draw = () => {
            if (isContainerOffscreen || !canvasWidth || !canvasHeight) {
                return;
            }

            const activeImages = getActiveImages();
            const imgA = activeImages[segIndex];
            const imgB = activeImages[segIndex + 1];

            ctx.clearRect(0, 0, canvasWidth, canvasHeight);

            if (weightA > 0 && imgA) {
                drawCoverImage(imgA, pixelSizeA, weightA);
            }
            if (weightB > 0 && imgB) {
                drawCoverImage(imgB, pixelSizeB, weightB);
            }
        };

        const checkVideoLoop = () => {
            if (!isIntersecting || isContainerOffscreen) {
                if (animationFrameId) {
                    cancelAnimationFrame(animationFrameId);
                    animationFrameId = null;
                }
                return;
            }

            const activeImages = getActiveImages();
            const imgA = activeImages[segIndex];
            const imgB = activeImages[segIndex + 1];

            const hasActiveVideo = (weightA > 0 && imgA && imgA.tagName === 'VIDEO' && !imgA.paused && !imgA.ended) ||
                                  (weightB > 0 && imgB && imgB.tagName === 'VIDEO' && !imgB.paused && !imgB.ended);

            if (hasActiveVideo) {
                if (!animationFrameId) {
                    const tick = () => {
                        draw();
                        const currentImages = getActiveImages();
                        const a = currentImages[segIndex];
                        const b = currentImages[segIndex + 1];
                        const stillActive = isIntersecting && !isContainerOffscreen &&
                            ((weightA > 0 && a && a.tagName === 'VIDEO' && !a.paused && !a.ended) ||
                             (weightB > 0 && b && b.tagName === 'VIDEO' && !b.paused && !b.ended));

                        if (stillActive) {
                            animationFrameId = requestAnimationFrame(tick);
                        } else {
                            animationFrameId = null;
                        }
                    };
                    animationFrameId = requestAnimationFrame(tick);
                }
            } else if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
                animationFrameId = null;
            }
        };

        const render = () => {
            updateLayout();
            draw();
            checkVideoLoop();
        };

        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    isIntersecting = entry.isIntersecting;
                    if (isIntersecting) {
                        checkVideoLoop();
                    } else if (animationFrameId) {
                        cancelAnimationFrame(animationFrameId);
                        animationFrameId = null;
                    }
                });
            }, { rootMargin: '100px 0px 100px 0px' });
            observer.observe(container);
        } else {
            isIntersecting = true;
        }

        updaters.push(render);
        render();
    });

    const updateStandalone = initStandaloneTeaserPixelate();
    if (updateStandalone) {
        updaters.push(updateStandalone);
        updateStandalone();
    }

    if (!updaters.length) {
        return;
    }

    let ticking = false;
    const onScrollOrResize = () => {
        if (!ticking) {
            requestAnimationFrame(() => {
                updaters.forEach((update) => update());
                ticking = false;
            });
            ticking = true;
        }
    };

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
});
